import { GetApp } from "../App"
import { Format } from "../App/Format"
import { IStoredCalculationTimeseries, IWeatherTimeseriesData } from "../Types"
import { Graph } from "./Graph/Graph"
import { GraphDef, GraphsDef, GraphTimeseries, GraphViewProps } from "./Graph/Params"

// The reusable Graph primitive and its pure GraphData geometry live in Views/Graph/;
// re-export them so existing consumers (and tests) can keep importing from ./GraphView.
export { Graph, GraphData } from "./Graph/Graph"

// Pure series builder: projects a stored weather timeseries into the {text,timeUtc,value}
// points a single Graph renders, dropping entries whose value is undefined/NaN.
export const GraphViewData = {
  generate(timeseries: IStoredCalculationTimeseries<IWeatherTimeseriesData> | undefined, graph: GraphDef | undefined): GraphTimeseries[] {
    if (!timeseries || !graph) {
      return [];
    }
    return timeseries.data.map((d: IWeatherTimeseriesData): GraphTimeseries | undefined => {
      const value = graph.value(d);
      if (value === undefined || isNaN(value)) {
        return undefined;
      }
      return {
        text: `${Format.dateInYear(d.timeUtc)} ${Format.timeUtc(d.timeUtc)}: ${graph.textValue(d)}`,
        timeUtc: d.timeUtc,
        value: value,
      };
    }).filter((t) => t != undefined);
  },
};

export function GraphView({ timeseries }: GraphViewProps) {
  const app = GetApp()!;
  const t = app.getT("GraphView");

  const graphs: GraphsDef = {
    pressure: {
      textValue: ((d: IWeatherTimeseriesData) => d.pressure ? d.pressure.toFixed(1) : undefined),
      value: ((d: IWeatherTimeseriesData) => d.pressure),
      valueScale: { max: 1085, min: 865 },
    },
    temperature: {
      textValue: ((d: IWeatherTimeseriesData) => d.temperature ? d.temperature.toFixed(0) : undefined),
      value: ((d: IWeatherTimeseriesData) => d.temperature),
    },
    windDirection: {
      legend: [
        { label: "N", value: 0 },
        { label: "NE", value: 45 },
        { label: "E", value: 90 },
        { label: "SE", value: 135 },
        { label: "S", value: 180 },
        { label: "SW", value: 225 },
        { label: "W", value: 270 },
        { label: "NW", value: 315 },
        { label: "N", value: 360 },
      ],
      textValue: ((d: IWeatherTimeseriesData) => d.windDirection ? t("graph_windDirection_unit", { direction: d.windDirection.toFixed(0) }) : undefined),
      value: ((d: IWeatherTimeseriesData) => d.windDirection),
      valueScale: { max: 360, min: 0 },
    },
    windSpeed: {
      textValue: ((d: IWeatherTimeseriesData) => d.windSpeed ? d.windSpeed.toFixed(0) : undefined),
      value: ((d: IWeatherTimeseriesData) => d.windSpeed),
    },
  };

  function gen(g: string): GraphTimeseries[] {
    return GraphViewData.generate(timeseries, graphs[g]);
  }

  return timeseries ? (
    <div className="GraphView">
      {Object.keys(graphs).map((g) => {
        return (
          <div key={`graph-${g}`} className="GraphView-Graph-container">
            <div className="GraphView-Graph-title">{t(`graph_${g}`)}</div>
            <Graph legend={graphs[g].legend} timeseries={gen(g)} valueScale={graphs[g].valueScale} />
          </div>
        );
      })}
    </div>
  ) : <></>;
}
